import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import '../superadmin/AddDistrict.css';
import { extractYMD } from '../../utils/dateOnly';

const RACE_OPTIONS = [
  'American Indian or Alaska Native',
  'Asian',
  'Black or African American',
  'Hawaiian or Other Pacific Islander',
  'White'
];

const ETHNICITY_OPTIONS = ['Hispanic/Latino', 'Non-Hispanic/Latino'];
const GENDER_OPTIONS = ['Male', 'Female'];

function normalizeGenderOption(value) {
  const v = String(value || '').trim();
  if (!v) return '';
  const byLabel = {
    male: 'Male',
    m: 'Male',
    female: 'Female',
    f: 'Female'
  };
  return byLabel[v.toLowerCase()] || v;
}

function normalizeRaceOption(value) {
  const v = String(value || '').trim();
  if (!v) return '';
  const byLabel = {
    caucasian: 'White',
    white: 'White',
    asian: 'Asian',
    'african american': 'Black or African American',
    'black or african american': 'Black or African American',
    'native american': 'American Indian or Alaska Native',
    'american indian': 'American Indian or Alaska Native',
    'american indian or alaska native': 'American Indian or Alaska Native',
    'pacific islander': 'Hawaiian or Other Pacific Islander',
    'hawaiian or other pacific islander': 'Hawaiian or Other Pacific Islander'
  };
  return byLabel[v.toLowerCase()] || v;
}

function normalizeEthnicityOption(value) {
  const v = String(value || '').trim();
  if (!v) return '';
  const byLabel = {
    'hispanic or latino': 'Hispanic/Latino',
    'hispanic/latino': 'Hispanic/Latino',
    'non-hispanic/latino': 'Non-Hispanic/Latino',
    'not hispanic or latino': 'Non-Hispanic/Latino',
    'non hispanic or latino': 'Non-Hispanic/Latino'
  };
  return byLabel[v.toLowerCase()] || v;
}

function formatDobForDisplay(isoDate) {
  // A DOB is a timezone-less calendar date; never route it through a UTC Date.
  const ymd = extractYMD(isoDate);
  if (!ymd) return isoDate ? String(isoDate) : '—';
  const monthName = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][ymd.month - 1];
  return `${monthName} ${ymd.day}, ${ymd.year}`;
}

const StudentIntakeForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    middle_name: '',
    last_4_ssn: '',
    date_of_birth: '',
    age: '',
    grade: '',
    gender: '',
    race: '',
    ethnicity: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditMode);
  /** 409 MATCHES_DELETED_STUDENT — user chooses restore or purge + recreate */
  const [deletedMatch, setDeletedMatch] = useState(null);
  const [resolveLoading, setResolveLoading] = useState(false);

  useEffect(() => {
    const loadStudent = async () => {
      if (!isEditMode) {
        setFetching(false);
        return;
      }

      try {
        setFetching(true);
        const response = await axios.get(`/api/student-intake/my-intake/${id}`);
        const student = response.data || {};

        setFormData({
          first_name: student.first_name || '',
          last_name: student.last_name || '',
          middle_name: student.middle_name || '',
          last_4_ssn: student.last_4_ssn || '',
          date_of_birth: student.date_of_birth ? String(student.date_of_birth).split('T')[0] : '',
          age: student.age ?? '',
          grade: student.grade || '',
          gender: normalizeGenderOption(student.gender),
          race: normalizeRaceOption(student.race),
          ethnicity: normalizeEthnicityOption(student.ethnicity)
        });
      } catch (err) {
        console.error('Error loading student intake record:', err);
        setError(err.response?.data?.error || 'Failed to load student record');
      } finally {
        setFetching(false);
      }
    };

    loadStudent();
  }, [id, isEditMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (error) setError('');
    if (deletedMatch) setDeletedMatch(null);
  };

  const validateForm = () => {
    if (!formData.first_name.trim()) {
      setError('First name is required');
      return false;
    }

    if (!formData.last_name.trim()) {
      setError('Last name is required');
      return false;
    }

    // No middle-name check: it is optional. See the field itself for why.

    if (!formData.last_4_ssn || !formData.last_4_ssn.trim()) {
      setError('Last 4 digits of SSN are required');
      return false;
    }

    if (!/^\d{4}$/.test(formData.last_4_ssn.trim())) {
      setError('Last 4 digits of SSN must be exactly 4 numbers');
      return false;
    }

    if (!formData.date_of_birth) {
      setError('Date of birth is required');
      return false;
    }

    const dob = new Date(formData.date_of_birth);
    const now = new Date();
    if (isNaN(dob.getTime())) {
      setError('Please enter a valid date of birth');
      return false;
    }
    if (dob > now) {
      setError('Date of birth cannot be in the future');
      return false;
    }

    if (formData.age === '' || formData.age === null || formData.age === undefined) {
      setError('Age is required');
      return false;
    }

    const parsedAge = Number(formData.age);
    if (!Number.isFinite(parsedAge) || parsedAge < 0 || parsedAge > 30) {
      setError('Age must be a number between 0 and 30');
      return false;
    }

    if (!formData.grade.trim()) {
      setError('Grade is required');
      return false;
    }

    const normalizedGender = normalizeGenderOption(formData.gender);
    if (!normalizedGender) {
      setError('Gender is required');
      return false;
    }
    if (!GENDER_OPTIONS.includes(normalizedGender)) {
      setError('Please select a valid gender option');
      return false;
    }

    const normalizedRace = normalizeRaceOption(formData.race);
    if (!normalizedRace) {
      setError('Race is required');
      return false;
    }
    if (!RACE_OPTIONS.includes(normalizedRace)) {
      setError('Please select a valid race option');
      return false;
    }

    const normalizedEthnicity = normalizeEthnicityOption(formData.ethnicity);
    if (!normalizedEthnicity) {
      setError('Ethnicity is required');
      return false;
    }
    if (!ETHNICITY_OPTIONS.includes(normalizedEthnicity)) {
      setError('Please select a valid ethnicity option');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Clean up the data
      const submitData = {
        ...formData,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        middle_name: formData.middle_name.trim(),
        last_4_ssn: formData.last_4_ssn.trim(),
        date_of_birth: formData.date_of_birth,
        age: formData.age === '' ? null : Number(formData.age),
        grade: formData.grade.trim(),
        gender: normalizeGenderOption(formData.gender),
        race: normalizeRaceOption(formData.race),
        ethnicity: normalizeEthnicityOption(formData.ethnicity)
      };

      if (isEditMode) {
        await axios.put(`/api/student-intake/my-intake/${id}`, submitData);
        navigate('/parent/student-intake');
      } else {
        const { data } = await axios.post('/api/student-intake', submitData);
        const next = new URLSearchParams();
        if (data?.district_student_relinked) next.set('reconnected', '1');
        navigate(`/parent/student-intake${next.toString() ? `?${next}` : ''}`);
      }
    } catch (err) {
      console.error('Error saving student intake:', err);
      const data = err.response?.data;
      if (!isEditMode && err.response?.status === 409 && data?.code === 'MATCHES_DELETED_STUDENT') {
        setDeletedMatch(data);
        setError('');
      } else if (err.response?.status === 409) {
        setError(data?.error || 'A student with this information already exists');
      } else if (err.response?.status === 403 && data?.code === 'ACTIVE_ENROLLMENT_YEAR_REQUIRED') {
        setError(
          data?.error ||
            'Your school has not set an active enrollment school year yet. Please contact your district.'
        );
      } else {
        setError(data?.error || 'Failed to create student record');
      }
    } finally {
      setLoading(false);
    }
  };

  const buildSubmitPayload = () => ({
    first_name: formData.first_name.trim(),
    last_name: formData.last_name.trim(),
    middle_name: formData.middle_name.trim(),
    last_4_ssn: formData.last_4_ssn.trim(),
    date_of_birth: formData.date_of_birth,
    age: formData.age === '' ? null : Number(formData.age),
    grade: formData.grade.trim(),
    gender: normalizeGenderOption(formData.gender),
    race: normalizeRaceOption(formData.race),
    ethnicity: normalizeEthnicityOption(formData.ethnicity)
  });

  const handleRestoreDeleted = async () => {
    if (!deletedMatch?.deleted_student_intake_id) return;
    const id = deletedMatch.deleted_student_intake_id;
    setResolveLoading(true);
    setError('');
    try {
      await axios.post(`/api/form-submissions/restore-student-intake/${id}`);
      await axios.put(`/api/student-intake/my-intake/${id}`, buildSubmitPayload());
      setDeletedMatch(null);
      navigate('/parent/student-intake');
    } catch (err) {
      console.error('Restore student intake error:', err);
      setError(err.response?.data?.error || 'Could not restore this student');
    } finally {
      setResolveLoading(false);
    }
  };

  const handlePurgeAndCreate = async () => {
    if (!deletedMatch?.deleted_student_intake_id) return;
    const id = deletedMatch.deleted_student_intake_id;
    setResolveLoading(true);
    setError('');
    try {
      await axios.delete(`/api/form-submissions/my-permanent-delete-student-intake/${id}`);
      const { data: created } = await axios.post('/api/student-intake', buildSubmitPayload());
      setDeletedMatch(null);
      const next = new URLSearchParams();
      if (created?.district_student_relinked) next.set('reconnected', '1');
      navigate(`/parent/student-intake${next.toString() ? `?${next}` : ''}`);
    } catch (err) {
      console.error('Purge and create student intake error:', err);
      setError(err.response?.data?.error || 'Could not replace the old record. Try again or contact support.');
    } finally {
      setResolveLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/parent/student-intake');
  };

  if (fetching) {
    return (
      <div className="add-district">
        <div>Loading student information...</div>
      </div>
    );
  }

  return (
    <div className="add-district">
      <div className="page-header">
        <h2>{isEditMode ? 'Edit Student Information' : 'Add New Student'}</h2>
        <p>
          {isEditMode
            ? 'Update this student record. Core values are synced to in-progress forms and tracked as amendments on submitted forms.'
            : 'Enter basic information about your student. You can complete forms for this student later.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="district-form">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="first_name">First Name *</label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              required
              maxLength="100"
              placeholder="Enter first name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="last_name">Last Name *</label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              required
              maxLength="100"
              placeholder="Enter last name"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="middle_name">Middle Name</label>
            {/* Optional, and it has to stay that way: plenty of students have no middle name,
                the column is nullable, and the server already stores it with
                normalizeOptionalText. The label carries no asterisk to match. */}
            <input
              type="text"
              id="middle_name"
              name="middle_name"
              value={formData.middle_name}
              onChange={handleInputChange}
              maxLength="100"
              placeholder="Enter middle name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="last_4_ssn">Last 4 Digits of SSN *</label>
            <input
              type="text"
              id="last_4_ssn"
              name="last_4_ssn"
              value={formData.last_4_ssn}
              onChange={handleInputChange}
              required
              maxLength="4"
              pattern="\d{4}"
              placeholder="1234"
              title="Enter exactly 4 digits"
            />
            <small style={{ color: '#6c757d', fontSize: '0.875em' }}>
              Required: Used for matching with school records
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="date_of_birth">Date of Birth *</label>
            <input
              type="date"
              id="date_of_birth"
              name="date_of_birth"
              value={formData.date_of_birth}
              onChange={handleInputChange}
              required
              max={new Date().toISOString().split('T')[0]} // Cannot be in the future
            />
            <small style={{ color: '#6c757d', fontSize: '0.875em' }}>
              Required: Used to calculate age and ensure accurate records
            </small>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="age">Age</label>
            <input
              type="number"
              id="age"
              name="age"
              value={formData.age}
              onChange={handleInputChange}
              required
              min="0"
              max="30"
              step="1"
              placeholder="Enter age"
            />
          </div>

          <div className="form-group">
            <label htmlFor="grade">Grade</label>
            <select
              id="grade"
              name="grade"
              value={formData.grade}
              onChange={handleInputChange}
              required
            >
              <option value="">Select grade</option>
              <option value="Preschool">Preschool</option>
              <option value="Kindergarten">Kindergarten</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
              <option value="6">6</option>
              <option value="7">7</option>
              <option value="8">8</option>
              <option value="9">9</option>
              <option value="10">10</option>
              <option value="11">11</option>
              <option value="12">12</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="gender">Gender</label>
            <select
              id="gender"
              name="gender"
              value={formData.gender}
              onChange={handleInputChange}
              required
            >
              <option value="">Select gender</option>
              {GENDER_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="race">Race</label>
            <select
              id="race"
              name="race"
              value={formData.race}
              onChange={handleInputChange}
              required
            >
              <option value="">Select race</option>
              {RACE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="ethnicity">Ethnicity</label>
            <select
              id="ethnicity"
              name="ethnicity"
              value={formData.ethnicity}
              onChange={handleInputChange}
              required
            >
              <option value="">Select ethnicity</option>
              {ETHNICITY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            className="btn-secondary"
            disabled={loading || resolveLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading || resolveLoading}
          >
            {loading ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Add Student')}
          </button>
        </div>
      </form>

      {!isEditMode && deletedMatch && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="deleted-student-match-title"
          onClick={() => !resolveLoading && setDeletedMatch(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: '1rem'
          }}
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: '8px',
              maxWidth: '440px',
              width: '100%',
              padding: '1.5rem',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
            }}
          >
            <h3 id="deleted-student-match-title" style={{ marginTop: 0, marginBottom: '0.75rem' }}>
              Matches a recently removed student
            </h3>
            <p style={{ color: '#444', lineHeight: 1.5, marginBottom: '1rem' }}>
              The information you entered matches a student that appears under{' '}
              <strong>Recently Deleted</strong> (same name and last four of SSN). You can bring that
              student back to your list, or permanently remove the old record and create this as a new
              student.
            </p>
            {deletedMatch.deleted_student && (
              <div
                style={{
                  background: '#f8f9fa',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  padding: '0.75rem 1rem',
                  marginBottom: '1.25rem',
                  fontSize: '0.95rem'
                }}
              >
                <div>
                  <strong>Previously removed:</strong>{' '}
                  {deletedMatch.deleted_student.last_name}, {deletedMatch.deleted_student.first_name}
                </div>
                <div style={{ marginTop: '0.35rem', color: '#555' }}>
                  Date of birth on file: {formatDobForDisplay(deletedMatch.deleted_student.date_of_birth)}
                </div>
              </div>
            )}
            {error && (
              <div className="error-message" style={{ marginBottom: '1rem' }}>
                {error}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button
                type="button"
                className="btn-primary"
                disabled={resolveLoading}
                onClick={handleRestoreDeleted}
                style={{ width: '100%' }}
              >
                {resolveLoading ? 'Working…' : 'Restore this student'}
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={resolveLoading}
                onClick={handlePurgeAndCreate}
                style={{ width: '100%' }}
              >
                Permanently remove old record and add new student
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={resolveLoading}
                onClick={() => setDeletedMatch(null)}
                style={{ width: '100%', marginTop: '0.25rem' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentIntakeForm;

